<template>
  <div
    class="prr-tree-row"
    role="treeitem"
    :aria-expanded="hasChildren ? (isExpanded ? 'true' : 'false') : undefined"
  >
    <div
      v-if="showReorderGap"
      class="prr-tree-row__gap prr-tree-row__gap--before"
      :class="{ 'prr-tree-row__gap--active': gapBeforeActive }"
      :style="{ paddingLeft: `${level * 24 + 28}px` }"
      data-testid="prr-tree-row-reorder-gap-before"
      @dragover.prevent="onGapDragOver($event, 'before')"
      @dragenter.prevent="onGapDragOver($event, 'before')"
      @dragleave="onGapDragLeave"
      @drop.prevent="onGapDrop($event, 'before')"
    />
    <div
      class="prr-tree-row__main"
      :class="[
        {
          'prr-tree-row__main--group': isGroup,
          'prr-tree-row__main--dragging': isBeingDragged,
        },
        dropStateClass,
      ]"
      data-testid="prr-tree-row-drop-target"
      @dragover.prevent="onDragOver"
      @dragenter.prevent="onDragEnter"
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
    >
      <div class="prr-tree-row__path" :style="{ paddingLeft: `${level * 24}px` }">
        <span
          v-if="!isGroup"
          class="prr-tree-row__drag-handle"
          :draggable="true"
          :aria-label="dragHandleLabel"
          :title="dragHandleTitle"
          @dragstart.stop="onDragStart"
          @dragend="onDragEnd"
        >
          <Icon name="grid_icon" size="normal" />
        </span>
        <button
          v-if="showExpandIcon"
          type="button"
          class="prr-tree-row__expand"
          :class="{ 'prr-tree-row__expand--disabled': !hasChildren }"
          :aria-label="
            hasChildren
              ? isExpanded
                ? `Collapse ${row.name}`
                : `Expand ${row.name}`
              : 'No children'
          "
          :disabled="!hasChildren"
          @click="toggleExpand"
        >
          <TreeExpansionIcon :state="expansionState" />
        </button>
        <OrgObjectCard
          :type="isGroup ? 'position' : row.type"
          :code="true"
          :code-value="cardCodeValue"
          :name="row.name"
          :icon="!isGroup"
          size="Medium"
          :allow_hover="isRole"
          :show-tooltip="!isGroup"
          :show-drill-down="isRole"
          :highlight-term="highlightTerm"
          :state="cardState"
          :show-disabled-hover="false"
          @openPopup="onRoleDrilldown"
        />
        <EXTooltip
          v-if="showRedundantChip"
          :title="redundantTooltipText"
          placement="top"
          class="prr-tree-row__redundant-tooltip"
        >
          <span
            class="storybook-badge storybook-badge--pending prr-tree-row__redundant-chip"
            data-testid="prr-tree-row-redundant-chip"
            >Redundant</span
          >
        </EXTooltip>
      </div>

      <div class="prr-tree-row__detach-cell">
        <StandardButton
          v-if="!isGroup"
          class="prr-tree-row__detach"
          size="small"
          type="red"
          variant="tertiary"
          right-icon="detachIcon"
          :label="detachLabel"
          :aria-label="detachLabel"
          :title="detachLabel"
          @click="onDetach"
        />
      </div>

      <div class="prr-tree-row__status">
        <OrgObjectStatusBadge :status="row.status" />
      </div>

      <div class="prr-tree-row__actions">
        <template v-if="!isGroup">
          <button
            type="button"
            class="prr-tree-row__icon-btn"
            :aria-label="`Open chatter for ${row.name}`"
            @click="emit('open-chatter', { rowId: row.id, rowType: row.type })"
          >
            <ChatterIcon />
          </button>
          <MenuIcon
            class="prr-tree-row__menu"
            :dropdown-items="rowMenuItems"
            :vertical="false"
            :show-tooltip="false"
            @onClick="onRowMenuItem"
          />
        </template>
      </div>
    </div>
    <div
      v-if="showReorderGap && isLastSibling"
      class="prr-tree-row__gap prr-tree-row__gap--after"
      :class="{ 'prr-tree-row__gap--active': gapAfterActive }"
      :style="{ paddingLeft: `${level * 24 + 28}px` }"
      data-testid="prr-tree-row-reorder-gap-after"
      @dragover.prevent="onGapDragOver($event, 'after')"
      @dragenter.prevent="onGapDragOver($event, 'after')"
      @dragleave="onGapDragLeave"
      @drop.prevent="onGapDrop($event, 'after')"
    />

    <div v-if="hasChildren && isExpanded" class="prr-tree-row__children">
      <PositionRolesResponsibilitiesTreeRow
        v-for="(child, childIndex) in row.children"
        :key="`${child.type}-${child.id}`"
        :row="child"
        :level="level + 1"
        :parent-type="row.type"
        :parent-id="row.id"
        :sibling-index="childIndex"
        :is-last-sibling="childIndex === row.children.length - 1"
        :expanded-ids="expandedIds"
        :highlight-term="highlightTerm"
        :is-impersonating="isImpersonating"
        v-on="$listeners"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "PositionRolesResponsibilitiesTreeRow",
};
</script>

<script setup>
import { computed, inject, onBeforeUnmount, ref } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import TreeExpansionIcon from "@components/TreeExpansionIcon/TreeExpansionIcon.vue";
import OrgObjectCard from "@components/OrgObjectCard/OrgObjectCard.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { isInvalidityPainted } from "@components/constants/invalidityReasons";

const props = defineProps({
  row: { type: Object, required: true },
  level: { type: Number, default: 0 },
  parentType: { type: String, default: null },
  parentId: { type: [Number, String], default: null },
  siblingIndex: { type: Number, default: 0 },
  isLastSibling: { type: Boolean, default: false },
  expandedIds: { type: Array, default: () => [] },
  isImpersonating: { type: Boolean, default: false },
  highlightTerm: { type: String, default: "" },
  redundantCoverageEnabled: { type: Boolean, default: false },
});

const emit = defineEmits([
  "toggle-expand",
  "detach",
  "open-chatter",
  "role-drilldown",
  "row-menu-action",
  "drag-start",
  "drag-state-change",
  "drop-row",
]);

const dragOverRow = ref(false);
const dragOverGap = ref(null);
const dragSource = ref(null);
const lastEmittedState = ref(null);
const expandTimer = ref(null);
const isBeingDragged = ref(false);
const dragSession = inject("prrDragSession", null);

const DRAG_MIME = "application/x-exiqtive-prr-convert";

onBeforeUnmount(() => {
  if (expandTimer.value) clearTimeout(expandTimer.value);
});

const isGroup = computed(() => props.row.type === "group");
const isRole = computed(() => props.row.type === "role");
const hasChildren = computed(
  () => Array.isArray(props.row.children) && props.row.children.length > 0
);
const isExpanded = computed(() => props.expandedIds.includes(props.row.id));
const showExpandIcon = computed(
  () => isGroup.value || props.row.type === "role" || hasChildren.value
);
const expansionState = computed(() => {
  if (!hasChildren.value) return "No Expand";
  return isExpanded.value ? "Expanded" : "Collapsed";
});
const detachLabel = computed(() => {
  if (props.parentType === "role") return "Detach from role";
  return "Detach from position";
});
const cardCodeValue = computed(() => {
  if (isGroup.value) return String(props.row.count ?? props.row.code ?? "");
  return props.row.code || "";
});
// ER-11635 — a row whose role/responsibility is draft, retired or archived renders its
// icon/name in the greyed "Disabled" style, mirroring the reference mapping in
// OrganizationRolesTable.vue (`orgObjectCardState`) and the greyed row treatment already
// used by PositionsTreeNavigator/TreeItem.vue. The row's own `archived` flag is honoured
// too, so an archived role that still reports status "active" is greyed as well.
// ER-9940 — a role the backend flags invalid (draft/active/retired, never archived) renders
// its name red via the "Invalid" state; responsibilities and group rows are never red.
const cardState = computed(() => {
  if (
    props.row.type === "role" &&
    isInvalidityPainted({
      isValid: props.row.is_valid,
      status: props.row.status,
      archived: props.row.archived === true,
    })
  ) {
    return "Invalid";
  }
  if (props.row.archived === true) return "Disabled";
  const status = String(props.row.status || "").toLowerCase();
  if (status === "draft" || status === "retired" || status === "archived") {
    return "Disabled";
  }
  return "Active";
});
// ER-12129 — the Position detail "⋯" menu must be the canonical org-object actions menu
// (same items/order/icons as DropDownMenu/dropdownMixin.js getReadinessCatalogsRoleItem /
// getReadinessCatalogsRessponsibilitiesItem), not the placeholder "Open chatter" / "Detach"
// list. "Detach" stays on the row as the red button (see the detach cell below), and the
// separate chatter icon button remains the chatter entry point.
const ROW_MENU_ICONS = {
  activate: "icons/dropdown/activate.svg",
  attach: "icons/dropdown/attach.svg",
  rename: "icons/dropdown/rename.svg",
  delete: "icons/dropdown/delete.svg",
  archive: "icons/dropdown/archive.svg",
  unarchive: "icons/dropdown/unarchive.svg",
};

const rowStatus = computed(() => String(props.row.status || "").toLowerCase());
const isArchivedRow = computed(
  () => props.row.archived === true || rowStatus.value === "archived"
);

const rowMenuItems = computed(() => {
  const items = [];
  const isResponsibility = props.row.type === "responsibility";
  const suffix = isResponsibility ? "responsibility" : "role";
  const attachResponsibilityItem = {
    name: "Attach Responsibility",
    value: "attach_responsibility_role",
    icon: ROW_MENU_ICONS.attach,
  };
  const renameItem = {
    name: "Rename",
    value: `rename_${suffix}`,
    icon: ROW_MENU_ICONS.rename,
  };

  if (rowStatus.value === "draft") {
    items.push({
      name: "Activate",
      value: `activate_${suffix}`,
      icon: ROW_MENU_ICONS.activate,
    });
    if (!isResponsibility) items.push(attachResponsibilityItem);
  } else if (rowStatus.value === "active") {
    items.push({
      name: "Retire",
      value: isResponsibility ? "retire_responsibility" : "retired_role",
      icon: ROW_MENU_ICONS.activate,
    });
    if (!isResponsibility) items.push(attachResponsibilityItem);
  } else if (rowStatus.value === "retired") {
    if (isArchivedRow.value) {
      items.push({
        name: "Unarchive",
        value: "unarchive_action",
        icon: ROW_MENU_ICONS.unarchive,
      });
    } else {
      items.push({
        name: "Re-activate",
        value: `re_activate_${suffix}`,
        icon: ROW_MENU_ICONS.activate,
      });
      items.push({
        name: "Archive",
        value: "archive_action",
        icon: ROW_MENU_ICONS.archive,
      });
    }
  } else if (rowStatus.value === "archived") {
    items.push({
      name: "Unarchive",
      value: "unarchive_action",
      icon: ROW_MENU_ICONS.unarchive,
    });
  } else if (rowStatus.value === "inactive") {
    items.push({
      name: "Re-activate",
      value: `re_activate_${suffix}`,
      icon: ROW_MENU_ICONS.activate,
    });
  }

  items.push(renameItem);

  if (rowStatus.value === "draft") {
    items.push({
      name: "Delete",
      value: `delete_${suffix}`,
      icon: ROW_MENU_ICONS.delete,
      negative: true,
    });
  }

  return items;
});

const resolvedParentType = computed(() => props.parentType || null);
const resolvedParentId = computed(() => props.parentId ?? props.row.parentId ?? null);

const isTopLevelRow = computed(() => !resolvedParentType.value);
const isTopLevelRole = computed(
  () => props.row.type === "role" && isTopLevelRow.value
);
const isTopLevelDirectResponsibility = computed(
  () => props.row.type === "responsibility" && isTopLevelRow.value
);

// ER-11818 / Rule 2 — a directly-attached Responsibility that is also carried by one or
// more ACTIVE Roles attached to the same Position is "Redundant". The backend exposes the
// covering Roles on the direct-attachment row as `also_covered_by_roles`
// ([{ role_id, role_code, role_name }]); the chip is derived from that field, never stored.
// It renders only on directly-attached rows (never role-sourced rows nested under a Role)
// and only while the MDP-585 direct-attach feature is enabled.
const coveringRoles = computed(() => {
  if (!props.redundantCoverageEnabled) return [];
  if (!isTopLevelDirectResponsibility.value) return [];
  const list = props.row?.also_covered_by_roles;
  return Array.isArray(list) ? list : [];
});
const showRedundantChip = computed(() => coveringRoles.value.length > 0);
const redundantTooltipText = computed(() =>
  coveringRoles.value
    .map((role) => {
      const code = role?.role_code ?? role?.code ?? "";
      const name = role?.role_name ?? role?.name ?? "";
      return `Covered by Role ${code} - ${name}`;
    })
    .join("\n")
);

const dragHandleLabel = computed(() => {
  if (props.row.type === "responsibility" && resolvedParentType.value === "role") {
    return `Drag ${props.row.name} to reorder`;
  }
  if (isTopLevelRole.value || isTopLevelDirectResponsibility.value) {
    return `Drag ${props.row.name} to reorder`;
  }
  if (props.row.type === "responsibility") return `Drag ${props.row.name} to attach to a role`;
  return `Drag ${props.row.name}`;
});
const dragHandleTitle = computed(() => {
  if (props.row.type === "responsibility" && resolvedParentType.value === "role") {
    return "Drag to reorder";
  }
  if (isTopLevelRole.value || isTopLevelDirectResponsibility.value) return "Drag to reorder";
  if (props.row.type === "responsibility") return "Drag to attach to a role";
  return "Drag";
});

const showReorderGap = computed(
  () =>
    (props.row.type === "responsibility" && resolvedParentType.value === "role") ||
    isTopLevelRole.value ||
    isTopLevelDirectResponsibility.value
);
const gapBeforeActive = computed(() => dragOverGap.value === "before");
const gapAfterActive = computed(() => dragOverGap.value === "after");

// Drop ON a role attaches a responsibility:
// - direct (position-attached) → convert to that role, or resolve a duplicate (dedupe)
//   when the target role already carries it (Rule 18a)
// - nested under a different role → attach to the target role
// Drop BETWEEN nested siblings reorders (gap targets).
function dropKind(source, targetRow) {
  if (!source || !targetRow) return null;
  if (source.rowId === targetRow.id) return null;
  if (source.rowType !== "responsibility" || targetRow.type !== "role" || targetRow.archived) {
    return null;
  }
  const status = String(targetRow.status || "").toLowerCase();
  if (status !== "draft" && status !== "active") return null;
  const alreadyOnTarget = (targetRow.children || []).some(
    (child) => String(child.id) === String(source.rowId)
  );
  if (source.parentType === "role") {
    // Role-sourced (nested under a Role) — Rule 19/20.
    if (alreadyOnTarget) return null;
    if (source.parentId != null && String(source.parentId) === String(targetRow.id)) {
      return null;
    }
    return "attach";
  }
  // Directly-attached (Rule 1) — Rule 18 convert, or new Rule 18a dedupe when the
  // target Role already carries this Responsibility (the accepted Rule 2 gap).
  if (alreadyOnTarget) return "dedupe";
  return "convert";
}

function isDropAllowed(source, targetRow) {
  return dropKind(source, targetRow) != null;
}

function isReorderGapAllowed(source) {
  if (!source || !showReorderGap.value) return false;
  if (source.rowId === props.row.id) return false;
  if (
    source.rowType === "responsibility" &&
    source.parentType === "role" &&
    source.parentId != null &&
    resolvedParentId.value != null &&
    String(source.parentId) === String(resolvedParentId.value)
  ) {
    return true;
  }
  if (isTopLevelRow.value && !source.parentType) {
    return source.rowType === "role" || source.rowType === "responsibility";
  }
  return false;
}

const dropAllowed = computed(() => isDropAllowed(dragSource.value, props.row));
const dropStateClass = computed(() => {
  if (!dragOverRow.value || !dragSource.value) return "";
  if (dragSource.value.rowId === props.row.id) return "";
  return dropAllowed.value
    ? "prr-tree-row__main--drop-allowed"
    : "prr-tree-row__main--drop-blocked";
});

function serializeSource() {
  return {
    rowId: props.row.id,
    rowType: props.row.type,
    parentType: resolvedParentType.value,
    parentId: resolvedParentId.value,
    attachmentId: props.row.attachmentId || props.row.id,
    name: props.row.name,
  };
}

function readSource(event) {
  const transfer = event.dataTransfer || event.originalEvent?.dataTransfer;
  if (transfer) {
    try {
      const raw =
        transfer.getData(DRAG_MIME) || transfer.getData("application/json") || "";
      if (raw) return JSON.parse(raw);
    } catch {
      // Browsers return an empty string from getData() during dragover; fall through.
    }
  }
  return dragSession?.source || null;
}

function toggleExpand() {
  if (!hasChildren.value) return;
  emit("toggle-expand", { rowId: props.row.id, expanded: !isExpanded.value });
}

function onRoleDrilldown() {
  if (!isRole.value) return;
  emit("role-drilldown", { id: props.row.id, name: props.row.name });
}

function onDetach() {
  emit("detach", {
    rowId: props.row.id,
    parentId: resolvedParentId.value,
    rowType: props.row.type,
  });
}

function onRowMenuItem(item) {
  if (!item?.value) return;
  emit("row-menu-action", { row: props.row, value: item.value });
}

function onDragStart(event) {
  const transfer = event.dataTransfer || event.originalEvent?.dataTransfer;
  if (!transfer) return;
  const source = serializeSource();
  if (dragSession) dragSession.source = source;
  transfer.setData(DRAG_MIME, JSON.stringify(source));
  transfer.setData("application/json", JSON.stringify(source));
  transfer.setData("text/plain", "prr-convert");
  transfer.effectAllowed = "move";
  // Suppress the browser's own (tiny, blurry) default drag ghost — the tree
  // shows its own hand/state cursor instead (DragAndDropInPositionsTreeNavigatorCursor),
  // same technique as EditCheckpointsTable's row drag.
  if (typeof transfer.setDragImage === "function") {
    const empty = document.createElement("div");
    empty.style.cssText = "width:1px;height:1px;position:fixed;left:-9999px;top:0;";
    document.body.appendChild(empty);
    transfer.setDragImage(empty, 0, 0);
    setTimeout(() => empty.remove(), 0);
  }
  isBeingDragged.value = true;
  emit("drag-start", { ...source, clientX: event.clientX, clientY: event.clientY });
}

function onDragEnd() {
  isBeingDragged.value = false;
}

function applyDragOver(event) {
  const source = readSource(event);
  if (!source) return;
  dragSource.value = source;
  dragOverRow.value = true;
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = isDropAllowed(source, props.row) ? "move" : "none";
  }
  if (hasChildren.value && !isExpanded.value) startExpandTimer();
  const allowed =
    source.rowId === props.row.id ? null : isDropAllowed(source, props.row);
  emitStateChange(allowed);
}

function onDragEnter(event) {
  applyDragOver(event);
}

function onDragOver(event) {
  applyDragOver(event);
}

function onDragLeave(event) {
  const current = event?.currentTarget;
  const related = event?.relatedTarget;
  // Moving to a descendant (e.g. a child control inside the row) is not a leave.
  if (current && related && current.contains(related)) return;
  if (!dragOverRow.value) return;
  dragOverRow.value = false;
  dragSource.value = null;
  clearExpandTimer();
  emitStateChange(null);
}

function emitStateChange(allowed, action = "Attach") {
  const next = `${allowed}:${allowed ? action : ""}`;
  if (lastEmittedState.value === next) return;
  lastEmittedState.value = next;
  emit("drag-state-change", allowed ? { allowed, action } : { allowed });
}

function startExpandTimer() {
  clearExpandTimer();
  expandTimer.value = setTimeout(() => {
    if (dragOverRow.value && hasChildren.value && !isExpanded.value) {
      emit("toggle-expand", { rowId: props.row.id, expanded: true });
    }
  }, 700);
}

function clearExpandTimer() {
  if (expandTimer.value) {
    clearTimeout(expandTimer.value);
    expandTimer.value = null;
  }
}

function onDrop(event) {
  clearExpandTimer();
  const source = readSource(event);
  const allowed = isDropAllowed(source, props.row);
  dragOverRow.value = false;
  dragSource.value = null;
  emitStateChange(null);
  if (!allowed) return;
  emit("drop-row", {
    kind: dropKind(source, props.row),
    source,
    targetRow: {
      rowId: props.row.id,
      roleId: props.row.roleId || props.row.id,
      name: props.row.name,
      status: props.row.status,
      type: props.row.type,
      parentId: resolvedParentId.value,
      parentType: resolvedParentType.value,
    },
  });
}

function onGapDragOver(event, position) {
  const source = readSource(event);
  if (!source) return;
  dragSource.value = source;
  const allowed = isReorderGapAllowed(source);
  dragOverGap.value = allowed ? position : null;
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = allowed ? "move" : "none";
  }
  emitStateChange(allowed ? true : source.rowId === props.row.id ? null : false, "Reorder");
}

function onGapDragLeave(event) {
  const current = event?.currentTarget;
  const related = event?.relatedTarget;
  if (current && related && current.contains(related)) return;
  if (!dragOverGap.value) return;
  dragOverGap.value = null;
  emitStateChange(null);
}

function onGapDrop(event, position) {
  const source = readSource(event);
  const allowed = isReorderGapAllowed(source);
  dragOverGap.value = null;
  emitStateChange(null);
  if (!allowed) return;
  emit("drop-row", {
    kind: source.rowType === "role" ? "reorder-role" : "reorder-resp",
    position,
    source,
    targetRow: {
      rowId: props.row.id,
      roleId: resolvedParentId.value,
      name: props.row.name,
      status: props.row.status,
      type: props.row.type,
      parentId: resolvedParentId.value,
      parentType: resolvedParentType.value,
    },
  });
}
</script>

<style scoped>
.prr-tree-row {
  display: flex;
  flex-direction: column;
}
.prr-tree-row__main {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) var(--prr-col-detach, 214px) var(--prr-col-status, 88px) var(
      --prr-col-actions,
      96px
    );
  align-items: center;
  height: 54px;
  min-height: 54px;
  padding: 0;
  border-bottom: 1px solid #eaecf0;
  box-sizing: border-box;
  transition: background-color 0.15s ease, outline-color 0.15s ease;
}
/* A valid drop target (responsibility onto a DRAFT/ACTIVE Role) is green. */
.prr-tree-row__main--drop-allowed {
  background: #ecfdf3;
  outline: 2px solid #12b76a;
  outline-offset: -2px;
}
/* Every other combination is blocked and is a no-op; the target leaf is red. */
.prr-tree-row__main--drop-blocked {
  background: #fef3f2;
  outline: 2px solid #f04438;
  outline-offset: -2px;
}
/* The whole row dims while it is the drag source, since the native drag ghost is suppressed. */
.prr-tree-row__main--dragging {
  opacity: 0.5;
}
.prr-tree-row__gap {
  position: relative;
  height: 8px;
  margin: -4px 0;
  z-index: 2;
  box-sizing: border-box;
}
.prr-tree-row__gap--active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 12px;
  top: 50%;
  height: 3px;
  margin-top: -1.5px;
  border-radius: 2px;
  background: #7f56d9;
}
.prr-tree-row__path {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  height: 100%;
  padding: 0 6px;
  box-sizing: border-box;
}
.prr-tree-row__redundant-tooltip {
  flex-shrink: 0;
}
/* ER-11818 / Rule 2 — the "Redundant" chip reuses the shared Badge palette
   (`storybook-badge` + `storybook-badge--pending` from
   organizations/statusBadge/badge.css — Badge sm / Warning) and only adds the
   row-level layout the badge does not own. */
.prr-tree-row__redundant-chip {
  flex-shrink: 0;
  white-space: nowrap;
  letter-spacing: 0;
}
.prr-tree-row__drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  cursor: grab;
  color: #98a2b3;
  flex-shrink: 0;
  user-select: none;
  -webkit-user-drag: element;
}
.prr-tree-row__drag-handle:active {
  cursor: grabbing;
}
.prr-tree-row__drag-handle :deep(svg),
.prr-tree-row__drag-handle :deep(img) {
  pointer-events: none;
  -webkit-user-drag: none;
}
.prr-tree-row__expand {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  flex-shrink: 0;
}
.prr-tree-row__expand--disabled {
  cursor: default;
}
.prr-tree-row__detach-cell {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 12px;
  box-sizing: border-box;
}
.prr-tree-row__detach {
  white-space: nowrap;
}
.prr-tree-row__status {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 24px 0 12px;
  box-sizing: border-box;
}
.prr-tree-row__actions {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;
  padding: 0 24px 0 12px;
  box-sizing: border-box;
}
.prr-tree-row__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  color: #667085;
  padding: 0;
}
.prr-tree-row__icon-btn :deep(.chatter-icon-com) {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.prr-tree-row__icon-btn:hover {
  background: #f2f4f7;
}
.prr-tree-row__menu {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}
.prr-tree-row__menu :deep(.ex-menu-icon),
.prr-tree-row__menu :deep(.ex-menu-icon-hori) {
  padding: 0 !important;
  margin: 0 !important;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}
.prr-tree-row__menu :deep(.ex-menu-icon:hover),
.prr-tree-row__menu :deep(.ex-menu-icon-hori:hover) {
  padding: 0 !important;
  margin: 0 !important;
  box-shadow: none;
  background-color: #f2f4f7 !important;
  border-radius: 4px;
}
.prr-tree-row__children {
  padding-left: 0;
}
</style>
